import type { CanonicalUnit, DishEstimate } from '@nutrigeist/shared';
import type { LoggableFood } from './LogFoodSheet.js';
import { formatGrams, formatKcal } from '../lib/format.js';

/** What picking a dish hands back: the food, at one portion. */
export interface DishPick {
  food: LoggableFood;
  quantity: { amount: number; unit: CanonicalUnit };
}

/**
 * Turns an estimated dish into something loggable.
 *
 * Sent inline rather than as a catalog reference — like a recipe — so the diary
 * entry keeps the numbers this estimate produced. One portion is the dish's
 * weight divided by the servings it was estimated for.
 */
function dishAsFood(dish: DishEstimate): LoggableFood {
  return {
    id: null,
    name: dish.name,
    brand: null,
    basis: 'per_100g',
    nutrients: dish.per100,
    serving:
      dish.servings > 0 && dish.totalGrams > 0
        ? { grams: dish.totalGrams / dish.servings, label: '1 Portion' }
        : null,
    density: null,
  };
}

/**
 * The ingredient breakdown behind an estimated dish.
 *
 * Shown in full rather than as a single number, because the amounts are the
 * model's guess and seeing them is what lets the user judge whether the total
 * is plausible — "700 g Hackfleisch" for one portion is obvious at a glance,
 * a bare "1120 kcal" is not.
 */
export function DishEstimateCard({
  dish,
  onPick,
  note,
}: {
  dish: DishEstimate;
  onPick: (pick: DishPick) => void;
  /** An extra caveat above the breakdown, for callers whose input was itself a guess. */
  note?: string;
}): JSX.Element {
  const usable = dish.totalGrams > 0;

  return (
    <div className="card stack">
      <div className="row-between">
        <div className="grow">
          <div className="card-title" style={{ marginBottom: 4 }}>
            Geschätztes Gericht
          </div>
          <strong>{dish.name}</strong>
        </div>
        <span className="badge badge-warning">Schätzung</span>
      </div>

      {note && <div className="banner banner-warning">{note}</div>}

      <div className="banner banner-warning">
        Die <strong>Zutatenmengen</strong> sind eine Schätzung des Modells. Die Nährwerte dahinter
        stammen aus dem Katalog. Prüfe die Mengen, bevor du das Gericht einträgst.
      </div>

      <div className="entry-list">
        {dish.components.map((component, index) => (
          <div className="entry" key={`${component.query}-${index}`}>
            <div className="grow">
              <div className="entry-name">{component.match?.food.name ?? component.query}</div>
              <div className="entry-meta">
                {formatGrams(component.grams)} g
                {component.match ? '' : ' · nicht im Katalog gefunden'}
              </div>
            </div>
            {component.match ? (
              <span className="entry-kcal">{formatKcal(component.totals.kcal)} kcal</span>
            ) : (
              <span className="badge badge-warning">?</span>
            )}
          </div>
        ))}
      </div>

      {dish.unresolvedCount > 0 && (
        <span className="hint">
          {dish.unresolvedCount} {dish.unresolvedCount === 1 ? 'Zutat wurde' : 'Zutaten wurden'}{' '}
          nicht gefunden und {dish.unresolvedCount === 1 ? 'ist' : 'sind'} nicht eingerechnet.
        </span>
      )}

      {usable ? (
        <>
          <div className="card card-tight">
            <div className="calorie-headline">
              <span className="value">{formatKcal(dish.totals.kcal)}</span>
              <span className="unit">
                kcal gesamt · {formatGrams(dish.totalGrams)} g ·{' '}
                {dish.servings === 1 ? '1 Portion' : `${dish.servings} Portionen`}
              </span>
            </div>
            <div className="macro-grid">
              <div className="macro-cell">
                <div className="macro-value">{formatGrams(dish.totals.protein)} g</div>
                <div className="macro-label">Eiweiß</div>
              </div>
              <div className="macro-cell">
                <div className="macro-value">{formatGrams(dish.totals.carbs)} g</div>
                <div className="macro-label">Kohlenh.</div>
              </div>
              <div className="macro-cell">
                <div className="macro-value">{formatGrams(dish.totals.fat)} g</div>
                <div className="macro-label">Fett</div>
              </div>
            </div>
          </div>

          <button
            className="btn btn-primary btn-block"
            onClick={() =>
              onPick({
                food: dishAsFood(dish),
                quantity: { amount: 1, unit: 'serving' },
              })
            }
          >
            Portion eintragen
          </button>
        </>
      ) : (
        <div className="banner banner-error">
          Keine der Zutaten war im Katalog zu finden — daraus lässt sich kein Nährwert berechnen.
        </div>
      )}
    </div>
  );
}
